import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * The safety net that should have been here from the start.
 *
 * A React render exception with nothing to catch it unmounts the entire tree —
 * the whole app goes white, sidebar and all. On a system whose argument is that
 * every figure is accountable, "one bad row blanks the screen and the user is
 * told nothing" is the wrong failure. This turns any render throw into a
 * CONTAINED panel with a way back, and — just as important — it LOGS the error
 * and the component stack, so the specific trigger stops being a mystery a user
 * has to report and becomes a line an operator can read.
 *
 * Two placements:
 *   - variant="app"  wraps the root (main.tsx). Last resort: if App or Shell
 *     itself throws, this shows a branded reload instead of a white page.
 *   - variant="page" wraps the content area (App.tsx), keyed by the current
 *     page. A crash in one report contains to the panel; the sidebar survives,
 *     and navigating away clears it — the same principle the matrix already
 *     applies to a failed branch ("a failure while expanding one branch must
 *     not blank the whole report").
 *
 * It is a class component because getDerivedStateFromError / componentDidCatch
 * are the only React API that catches a render throw — hooks cannot.
 */
export class ErrorBoundary extends Component<
  {
    children: ReactNode
    /** 'app' = full-viewport root net; 'page' = contained content-area panel. */
    variant?: 'app' | 'page'
    /**
     * When any value here changes, a caught error is cleared. Pass the current
     * page id so switching pages recovers without a manual retry.
     */
    resetKeys?: unknown[]
  },
  { error: Error | null }
> {
  state = { error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The diagnostic hook. Console today; the place remote logging would attach.
    // The component stack is what turns "it went white while I was drilling"
    // into a named component and line.
    console.error(
      '[MBI] Uncaught render error — contained by ErrorBoundary.\n' +
        `message: ${error.message}\n` +
        `component stack:${info.componentStack ?? ' (none)'}`,
      error,
    )
  }

  componentDidUpdate(prev: Readonly<{ resetKeys?: unknown[] }>) {
    if (!this.state.error) return
    const a = prev.resetKeys ?? []
    const b = this.props.resetKeys ?? []
    if (a.length !== b.length || a.some((v, i) => v !== b[i])) {
      this.setState({ error: null })
    }
  }

  private reset = () => this.setState({ error: null })

  render() {
    if (!this.state.error) return this.props.children

    const body = (
      <div className="mx-auto max-w-2xl overflow-hidden rounded-md border border-l-[3px] border-(--color-line) border-l-(--color-overdue) bg-(--color-surface) shadow-(--shadow-card)">
        <div className="px-6 py-5">
          <h2 className="mb-2 text-(length:--text-lg) font-semibold text-(--color-ink)">
            This view ran into a problem
          </h2>
          <p className="text-(length:--text-base) leading-relaxed text-(--color-ink-2)">
            Something on this screen failed to render. Nothing was changed, and the rest of the
            platform is unaffected — your other reports and the navigation still work.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="rounded-md border border-(--color-line-strong) bg-(--color-navy-700) px-3 py-1.5 text-(length:--text-small) font-semibold text-white transition-colors hover:bg-(--color-navy-800)"
            >
              Try this view again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md border border-(--color-line-strong) px-3 py-1.5 text-(length:--text-small) font-semibold text-(--color-ink-2) transition-colors hover:bg-(--color-surface-2)"
            >
              Reload the page
            </button>
          </div>
          <p className="mt-3 text-(length:--text-small) text-(--color-ink-3)">
            If it keeps happening, note what you were doing and tell an administrator — the details
            are in the browser console for support.
          </p>
        </div>
      </div>
    )

    // App variant fills the viewport (Shell may itself be gone); page variant
    // sits inside the content area the Shell already laid out.
    return this.props.variant === 'app' ? (
      <div className="grid min-h-dvh place-items-center bg-(--color-surface-2) p-6">{body}</div>
    ) : (
      <div className="py-6">{body}</div>
    )
  }
}
